// Versioned, persisted privacy choices.
// The app runs no analytics, advertising, or cross-site tracking technology, so there are no
// non-essential storage categories to toggle. The one meaningful choice a visitor can make is to
// opt out of any "sale" or "sharing" of personal information as those terms are defined broadly by
// US state privacy laws. That choice is persisted here and transmitted with form submissions so it
// can be honored downstream.

export const PRIVACY_CHOICES_VERSION = 1
const STORAGE_KEY = "privacyChoices.v1"

export interface PrivacyChoices {
  version: number
  /** true = visitor opted out of sale/sharing of personal information */
  saleSharingOptOut: boolean
  /** where the current value came from */
  source: "default" | "user" | "gpc"
  updatedAt: string
}

/** Reads the browser's Global Privacy Control signal, if the browser exposes one. */
export function getGpcSignal(): boolean {
  if (typeof navigator === "undefined") return false
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean | string }
  return nav.globalPrivacyControl === true || nav.globalPrivacyControl === "1"
}

function readStored(): PrivacyChoices | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PrivacyChoices>
    if (parsed.version !== PRIVACY_CHOICES_VERSION) return null
    if (typeof parsed.saleSharingOptOut !== "boolean") return null
    return {
      version: PRIVACY_CHOICES_VERSION,
      saleSharingOptOut: parsed.saleSharingOptOut,
      source: parsed.source === "user" || parsed.source === "gpc" ? parsed.source : "user",
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
    }
  } catch {
    return null
  }
}

/**
 * Current effective choices. A saved choice wins over an unset default; a GPC signal turns the
 * opt-out on when the visitor has not explicitly saved a different choice.
 */
export function getPrivacyChoices(): PrivacyChoices {
  const stored = readStored()
  if (stored) return stored
  if (getGpcSignal()) {
    return {
      version: PRIVACY_CHOICES_VERSION,
      saleSharingOptOut: true,
      source: "gpc",
      updatedAt: new Date().toISOString(),
    }
  }
  return {
    version: PRIVACY_CHOICES_VERSION,
    saleSharingOptOut: false,
    source: "default",
    updatedAt: new Date().toISOString(),
  }
}

export function setSaleSharingOptOut(optOut: boolean): PrivacyChoices {
  const next: PrivacyChoices = {
    version: PRIVACY_CHOICES_VERSION,
    saleSharingOptOut: optOut,
    source: "user",
    updatedAt: new Date().toISOString(),
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // storage blocked: the choice still applies for this page session
  }
  return next
}

/** Clears the saved choice so the browser signal (or the default) applies again. */
export function resetPrivacyChoices(): PrivacyChoices {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  return getPrivacyChoices()
}
